import { z } from "zod";

import type { DragonTile } from "@/types/dragon-tiles";
import type { JsonSchema, UISchemaElement } from "@jsonforms/core";

export const ConnectorSchema = z.object({
  name: z.string(),
  id: z.string(),
  logoUrl: z.string(),
  type: z.string(),
  description: z.string().optional(),
});

export type Connector = z.infer<typeof ConnectorSchema>;

export const ExternalInstallConfigSchema = z.object({
  type: z.literal("external"),
  installationUrl: z.string().min(1),
});

export type ExternalInstallConfig = z.infer<typeof ExternalInstallConfigSchema>;

export const InstallFieldSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  secret: z.boolean().optional(),
});

export type InstallField = z.infer<typeof InstallFieldSchema>;

export const InternalInstallConfigSchema = z.object({
  type: z.literal("internal"),
  // Optional: an internal install with no fields is still installable — the
  // backend omits empty arrays (omitempty), so requiring the key would
  // wrongly demote such connectors to "coming soon" (FOG-2142).
  fields: z.array(z.string()).optional(),
  // Rich install inputs (friendly label + secret masking), additive over the
  // legacy key-only `fields`. The inner .catch(undefined) makes a malformed
  // installFields degrade to the legacy `fields` path instead of tripping the
  // union-level .catch that demotes the whole connector to "coming soon".
  // Duplicate keys count as malformed: the install form keys its state and
  // submit payload by field.key, so later entries would clobber earlier ones.
  installFields: z
    .array(InstallFieldSchema)
    .refine(
      (fields) => new Set(fields.map((f) => f.key)).size === fields.length,
    )
    .optional()
    .catch(undefined),
  installationUrl: z.string().min(1),
});

export type InternalInstallConfig = z.infer<typeof InternalInstallConfigSchema>;

export const InstallableConnectorSchema = ConnectorSchema.extend({
  // Coming soon = installConfigs omitted or exactly `{}` (the backend
  // serializes the field as a non-optional struct — integrations-bridge
  // apps/connectors store.go — so coming-soon connectors arrive as `{}`).
  // The catch also downgrades a malformed config (backend's contract to fix,
  // FOG-2142) to a placeholder rather than failing the whole catalog parse
  // over one bad connector.
  installConfigs: z
    .union([ExternalInstallConfigSchema, InternalInstallConfigSchema])
    .optional()
    .catch(undefined),
});

export type InstallableConnector = z.infer<typeof InstallableConnectorSchema>;

export const InstallableConnectorsResponseSchema = z.union([
  InstallableConnectorSchema.array(),
  z.object({
    connectors: InstallableConnectorSchema.array(),
  }),
  z.object({
    installableConnectors: InstallableConnectorSchema.array(),
  }),
  z.object({
    data: InstallableConnectorSchema.array(),
  }),
]);

export const CustomerMetadataTagSchema = z.object({
  identifier: z.string(),
  label: z.string(),
  preview: z.string(),
  categoryLabel: z.string(),
  tagType: z.enum(["text", "url"]).optional(),
  connectorId: z.string().optional(),
  productLine: z.string().optional(),
});

export type CustomerMetadataTag = z.infer<typeof CustomerMetadataTagSchema>;

export const InstalledConnectorSchema = ConnectorSchema.extend({
  warnings: z.array(z.string()).optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  events: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        enabled: z.boolean().optional(),
        category: z.string().optional(),
        mergeTags: z.array(CustomerMetadataTagSchema).optional(),
      }),
    )
    .optional(),
  customerMetadataTags: z.array(CustomerMetadataTagSchema).optional(),
  // Present only for platforms whose webhooks cannot be registered through an
  // API (skio, talkable, aftership): a human must paste `webhookUrl` into the
  // platform's own dashboard. The URL embeds the integration id, so it cannot
  // come from the static settings form — this response is the only place it is
  // available.
  webhookUrl: z.string().optional(),
  webhookSetupInstructions: z.string().optional(),
  // When a webhook that PASSED verification was last received. Absent means
  // none ever has, which for a hand-registered webhook is the one visible
  // symptom of every setup mistake — treat it as "not confirmed working", not
  // as "quiet".
  lastWebhookAt: z.string().optional(),
});

export type InstalledConnector = z.infer<typeof InstalledConnectorSchema>;
export type ConnectorEvent = InstalledConnector["events"] extends
  | Array<infer T>
  | undefined
  ? T
  : never;

// BYOC custom events (connectors service: /{siteId}/byoc/events).
// `id` is a server-generated immutable UUID; `name` is the mutable display
// value; enabled state lives in the integration's activeEvents on the backend.
export const ByocEventSchema = z.object({
  id: z.string(),
  name: z.string(),
  enabled: z.boolean(),
  createdAt: z.string(),
});

export type ByocEvent = z.infer<typeof ByocEventSchema>;

export const ByocEventsResponseSchema = z.object({
  events: ByocEventSchema.array(),
});

export const InstalledConnectorsResponseSchema = z.union([
  InstalledConnectorSchema.array(),
  z.object({
    connectors: InstalledConnectorSchema.array(),
  }),
  z.object({
    installedConnectors: InstalledConnectorSchema.array(),
  }),
  z.object({
    data: InstalledConnectorSchema.array(),
  }),
]);

export interface ConnectorTile extends DragonTile {
  events?: ConnectorEvent[];
  logoUrl: string;
}

export interface ConnectorInstallField {
  key: string;
  value: string;
}

export const InternalConnectorInstallPayloadSchema = z.record(
  z.string(),
  z.string(),
);

export type InternalConnectorInstallPayload = z.infer<
  typeof InternalConnectorInstallPayloadSchema
>;

export const ExternalConnectorInstallResponseSchema = z.object({
  redirectUrl: z.string(),
});

export type ExternalConnectorInstallResponse = z.infer<
  typeof ExternalConnectorInstallResponseSchema
>;

export type ConnectorJsonFormsConfigData = Record<string, unknown>;

export const ConnectorConfigResponseSchema = z.object({
  schema: z.custom<JsonSchema>(
    (value) => typeof value === "object" && value !== null,
  ),
  uischema: z.custom<UISchemaElement>(
    (value) => typeof value === "object" && value !== null,
  ),
  data: z.record(z.string(), z.unknown()),
});

export type ConnectorConfigResponse = z.infer<
  typeof ConnectorConfigResponseSchema
>;

export const SaveConnectorConfigPayloadSchema = z.record(
  z.string(),
  z.unknown(),
);

export type SaveConnectorConfigPayload = z.infer<
  typeof SaveConnectorConfigPayloadSchema
>;
